import React, { useState } from 'react';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';
import { Text } from '../Text/Text';
import { Switch } from '../Switch/Switch';

interface CABShareModalProps {
  projectId: string;
  shareableLinkEnabled: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSave: (enabled: boolean) => Promise<void>;
}

export const CABShareModal: React.FC<CABShareModalProps> = ({
  projectId,
  shareableLinkEnabled: initialEnabled,
  isOpen,
  onClose,
  onSave,
}) => {
  const [shareableLinkEnabled, setShareableLinkEnabled] = useState(initialEnabled);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  };

  const handleCopyLink = async () => {
    try {
      const currentHost = window.location.origin;
      const shareableLink = `${currentHost}/project/${projectId}/cab`;
      
      await navigator.clipboard.writeText(shareableLink);
      setLinkCopied(true);
      
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      alert('Failed to copy link to clipboard. Please try again.');
    }
  };

  const handleShareableLinkToggle = async (enabled: boolean) => {
    setShareableLinkEnabled(enabled);
    try {
      setIsSaving(true);
      await onSave(enabled);
    } catch (error) {
      console.error('Failed to update shareable link setting:', error);
      setShareableLinkEnabled(!enabled);
      alert('Failed to update shareable link setting. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-start justify-end bg-alpha-black-70 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`bg-bg-primary h-full flex flex-col w-full max-w-[500px] shadow-bg-v2-2xl border-l border-border-secondary overflow-y-auto ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-bg-primary flex gap-bg-v2-md items-start p-bg-v2-3xl border-b border-border-secondary shrink-0">
          <div className="flex-1 flex flex-col gap-bg-v2-xxs">
            <h2>
              <Text variant="text-lg" weight="semibold" color="primary" className="font-bg-v2-sans">
                Share CAB
              </Text>
            </h2>
            <Text variant="text-sm" weight="regular" color="secondary" className="font-bg-v2-sans">
              Enable or disable the shareable link for this CAB.
            </Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            aria-label="Close modal"
          >
            <Icon name={IconName.XClose} size="md" inheritColor />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-bg-v2-xl items-start px-bg-v2-xl py-bg-v2-xl flex-1">
          {/* Shareable Link Access Section */}
          <div className="bg-bg-primary border border-border-secondary rounded-bg-v2-xl p-bg-v2-2xl flex flex-col gap-bg-v2-xl w-full">
            <div className="flex items-start justify-between w-full gap-bg-v2-xl">
              <div className="flex flex-col gap-bg-v2-xs flex-1">
                <Text variant="text-md" weight="semibold" color="primary" className="font-bg-v2-sans">
                  Public Access
                </Text>
                <Text variant="text-sm" weight="regular" color="tertiary" className="font-bg-v2-sans">
                  Anyone with the link can view this CAB
                </Text>
              </div>
              <div className="flex items-center gap-bg-v2-lg shrink-0">
                <Text
                  variant="text-sm"
                  weight="semibold"
                  color={shareableLinkEnabled ? "brand-primary" : "tertiary"}
                  className="font-bg-v2-sans"
                >
                  {shareableLinkEnabled ? 'On' : 'Off'}
                </Text>
                <Switch
                  checked={shareableLinkEnabled}
                  onChange={(e) => handleShareableLinkToggle(e.target.checked)}
                  size="md"
                  disabled={isSaving}
                />
              </div>
            </div>
            
            {shareableLinkEnabled && (
              <>
                <div className="bg-border-secondary h-px w-full" />
                <div className="flex flex-col gap-bg-v2-sm w-full">
                  <div className="relative w-full flex items-center bg-bg-primary-alt border border-border-primary rounded-bg-v2-md shadow-bg-v2-xs">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/project/${projectId}/cab`}
                      className="flex-1 bg-transparent px-bg-v2-lg py-bg-v2-md font-bg-v2-sans font-bg-v2-regular text-bg-v2-sm text-text-secondary truncate cursor-default focus:outline-none border-none outline-none"
                      tabIndex={-1}
                    />
                    <button
                      onClick={handleCopyLink}
                      className={`flex items-center justify-center p-2 mr-2 rounded-bg-v2-md transition-all duration-200 shrink-0 ${
                        linkCopied 
                          ? 'bg-bg-brand-primary-alt text-text-brand-primary' 
                          : 'hover:bg-bg-primary-hover text-text-secondary hover:text-text-primary'
                      }`}
                      aria-label="Copy link"
                      type="button"
                    >
                      <Icon 
                        name={linkCopied ? IconName.Check : IconName.Copy} 
                        size="md" 
                        inheritColor 
                      />
                    </button>
                  </div>
                  {linkCopied && (
                    <Text variant="text-xs" weight="medium" color="brand-primary" className="font-bg-v2-sans">
                      ✓ Link copied to clipboard
                    </Text>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
